import { PageHeader } from "../components/layout/PageHeader";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { BookOpen, FileImage, Presentation, Mic, Mail, MessageSquare } from "lucide-react";

const CREATION = [
  { day: "Mon", projects: 12, exports: 4 },
  { day: "Tue", projects: 18, exports: 7 },
  { day: "Wed", projects: 22, exports: 9 },
  { day: "Thu", projects: 16, exports: 6 },
  { day: "Fri", projects: 24, exports: 11 },
  { day: "Sat", projects: 9, exports: 3 },
  { day: "Sun", projects: 6, exports: 2 },
];

const CAMPAIGNS = [
  { day: "Mon", opens: 420, clicks: 180, sms: 60 },
  { day: "Tue", opens: 510, clicks: 220, sms: 90 },
  { day: "Wed", opens: 680, clicks: 290, sms: 110 },
  { day: "Thu", opens: 590, clicks: 240, sms: 80 },
  { day: "Fri", opens: 720, clicks: 320, sms: 140 },
  { day: "Sat", opens: 280, clicks: 110, sms: 30 },
  { day: "Sun", opens: 210, clicks: 90, sms: 20 },
];

const AI_USAGE = [
  { name: "Claude", value: 12900 },
  { name: "GPT", value: 18450 },
  { name: "Gemini", value: 8200 },
  { name: "Image", value: 540 },
];

const COLORS = ["#27E6D1", "#B9FF2F", "#0BAF83", "#C7FFF3"];

export default function Analytics() {
  return (
    <div data-testid="analytics-page" className="space-y-8">
      <PageHeader
        kicker="Analytics"
        title="Creation, campaigns, and AI"
        description="Track what you're shipping, who's engaging, and how much your AI is doing for you."
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Tile icon={BookOpen} label="Books" value="14" />
        <Tile icon={FileImage} label="Flyers" value="38" />
        <Tile icon={Presentation} label="Decks" value="22" />
        <Tile icon={Mic} label="Audio" value="9" />
        <Tile icon={Mail} label="Emails" value="14.5K" />
        <Tile icon={MessageSquare} label="SMS" value="2.1K" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Creation activity" eyebrow="7-day">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={CREATION}>
              <defs>
                <linearGradient id="aqua" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#27E6D1" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#27E6D1" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="lime" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#B9FF2F" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#B9FF2F" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="day" stroke="#9CB7B2" fontSize={12} />
              <YAxis stroke="#9CB7B2" fontSize={12} />
              <Tooltip contentStyle={{ background: "#0B1F1F", border: "1px solid rgba(39,230,209,0.25)", borderRadius: 8, color: "#F4FFFC" }} />
              <Area type="monotone" dataKey="projects" stroke="#27E6D1" fill="url(#aqua)" strokeWidth={2} />
              <Area type="monotone" dataKey="exports" stroke="#B9FF2F" fill="url(#lime)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Campaign engagement" eyebrow="7-day">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={CAMPAIGNS}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="day" stroke="#9CB7B2" fontSize={12} />
              <YAxis stroke="#9CB7B2" fontSize={12} />
              <Tooltip contentStyle={{ background: "#0B1F1F", border: "1px solid rgba(39,230,209,0.25)", borderRadius: 8, color: "#F4FFFC" }} />
              <Bar dataKey="opens" fill="#27E6D1" radius={[6, 6, 0, 0]} />
              <Bar dataKey="clicks" fill="#0BAF83" radius={[6, 6, 0, 0]} />
              <Bar dataKey="sms" fill="#B9FF2F" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Panel title="AI usage breakdown" eyebrow="This cycle">
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie data={AI_USAGE} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={4}>
              {AI_USAGE.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="#0B1F1F" />
              ))}
            </Pie>
            <Tooltip contentStyle={{ background: "#0B1F1F", border: "1px solid rgba(39,230,209,0.25)", borderRadius: 8, color: "#F4FFFC" }} />
            <Legend wrapperStyle={{ color: "#9CB7B2", fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      </Panel>
    </div>
  );
}

function Tile({ icon: I, label, value }) {
  return (
    <div className="rounded-2xl p-4 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">
      <I className="h-4 w-4 text-[var(--pl-aqua)]" />
      <div className="text-[10px] uppercase tracking-[0.22em] mt-1.5 text-[var(--pl-text-muted)]">{label}</div>
      <div className="font-display text-xl font-semibold text-[var(--pl-text)]">{value}</div>
    </div>
  );
}

function Panel({ title, eyebrow, children }) {
  return (
    <div className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">{eyebrow}</div>
      <div className="font-display text-base font-medium text-[var(--pl-text)] mb-3">{title}</div>
      {children}
    </div>
  );
}
